








































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState, SortOrder, SortSpecs } from '@/store/types/general.types';
import {
  JbicGroupListColumns,
  JbicGroupParams,
  JbicGroups,
  JbicGroupStatus
} from '@/store/modules/admin/types/group.types';
import { Pagination } from '@/store/modules/admin/types/admin.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import GroupsList from './components/GroupsList.vue';
import { REGION_COUNTRIES, RegionKey } from '@/utils/regions.util';
import { toTitleCase } from '@/jbi-shared/util/string.utils';

const getInitFormParams = (): JbicGroupParams => ({
  legalName: '',
  region: 'All Regions',
  country: 'All',
  status: JbicGroupStatus.ALL,
  page: 1,
  limit: 50,
  sortColumn: 'g.name', // legalName
  sortOrder: SortOrder.ASC
});

@Component({
  components: {
    BasePaginatorHoc,
    GroupsList
  }
})
export default class GroupsTab extends Vue {
  // get jbic groups
  @Action('admin/getJbicGroupsWithPagination')
  getJbicGroupsWithPagination!: (params?: JbicGroupParams) => void;

  @State(({ admin }: RootState) => admin.apiState.getJbicGroupsWithPagination)
  jbicGroupsWithPaginationApiState!: ApiState;

  @State(({ admin }: RootState) => admin.jbicGroupsWithPagination)
  jbicGroupsWithPagination!: Pagination<JbicGroups>;

  get GroupsList() {
    return GroupsList;
  }

  get JbicGroupStatus() {
    return JbicGroupStatus;
  }

  get RegionList(): Record<string, string> {
    const regions: Record<string, string> = {};

    Object.keys(REGION_COUNTRIES).forEach((region) => {
      regions[region] = toTitleCase(region, ' ');
    });

    return regions;
  }

  get ApprovalStatus(): Record<string, string> {
    const statuses: Record<string, string> = {};

    Object.keys(JbicGroupStatus).forEach((status) => {
      statuses[status] = toTitleCase(status, '_');
    });

    return statuses;
  }

  get AllCountryList() {
    const allCountries: string[] = [];

    Object.values(REGION_COUNTRIES).forEach((region) => {
      allCountries.push(...Object.values(region));
    });

    return allCountries.sort();
  }

  // pagination
  public perPage = 50;
  public page = 1;
  public sortColumn: string = 'name';
  public sortOrder: SortOrder = SortOrder.ASC;
  public totalItems: number = 0;

  // filter
  public filterParams: JbicGroupParams = getInitFormParams();
  public expandGroupFilter = false;

  // params
  public countries: string[] = this.AllCountryList;
  public groups: JbicGroupListColumns[] = [];

  mounted() {
    this.getJbicGroupsWithPagination({
      legalName: this.filterParams.legalName,
      region: this.filterParams.region,
      country: this.filterParams.country,
      status: this.filterParams.status,
      limit: this.filterParams.limit,
      page: this.filterParams.page,
      sortColumn: this.filterParams.sortColumn,
      sortOrder: this.filterParams.sortOrder
    });
  }

  resetFilter() {
    this.filterParams = getInitFormParams();
  }

  @Watch('jbicGroupsWithPaginationApiState')
  onJbicGroupsWithPaginationApiState() {
    if (
      !this.jbicGroupsWithPaginationApiState ||
      this.jbicGroupsWithPaginationApiState.loading ||
      !this.jbicGroupsWithPaginationApiState.success ||
      !this.jbicGroupsWithPagination
    ) {
      return;
    }

    const groups: JbicGroups[] = this.jbicGroupsWithPagination.items;
    this.totalItems = this.jbicGroupsWithPagination.totalItems;

    this.groups = [];
    groups.forEach((item: JbicGroups) => {
      const group: JbicGroupListColumns = {
        groupId: item.id,
        legalName: item.name,
        path: item.path,
        region: item.region,
        country: item.country,
        pendingApprovalCount: item.pendingApprovalCount,
        uniqueMembersCount: item.memberCount
      };

      this.groups.push(group);
    });
  }

  @Watch('filterParams', { deep: true })
  @Debounce(500)
  onFilterParams() {
    // filter countries based on region
    if (this.filterParams.region) {
      if (this.filterParams.region === 'All Regions') {
        // if All Regions, returns all countries
        this.countries = this.AllCountryList;
      } else {
        // if specific, returns filtered countries by region
        const selectedRegion: RegionKey = (Object.keys(this.RegionList).find(
          (key) => this.RegionList[key] === this.filterParams.region
        ) || 'ALL REGIONS') as RegionKey;
        this.countries = REGION_COUNTRIES[selectedRegion];

        // if new region does not include country, then reset country to All
        if (
          this.filterParams.country &&
          !this.countries.includes(this.filterParams.country)
        ) {
          this.filterParams.country = 'All';
        }
      }
      this.countries.sort();
    }

    const params: JbicGroupParams = {
      legalName: this.filterParams.legalName,
      region: this.filterParams.region,
      country: this.filterParams.country,
      status: this.filterParams.status,
      limit: this.perPage,
      page: this.page,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    };

    this.getJbicGroupsWithPagination(params);
  }

  public async handleSort(data: SortSpecs) {
    this.sortColumn = data.sortColumn;
    this.sortOrder = data.sortOrder;

    const params: JbicGroupParams = {
      legalName:
        (this.$route.query.legalName as string) || this.filterParams.legalName,
      region: (this.$route.query.region as string) || this.filterParams.region,
      country:
        (this.$route.query.country as string) || this.filterParams.country,
      status: (this.$route.query.status as string) || this.filterParams.status,
      limit: +this.$route.query.limit || this.perPage,
      page: +this.$route.query.page || this.page,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    };

    this.getJbicGroupsWithPagination(params);

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Groups',
        legalName: this.$route.query.legalName || this.filterParams.legalName!,
        region: this.$route.query.region || this.filterParams.region!,
        country: this.$route.query.country || this.filterParams.country!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: (this.$route.query.limit as string) || this.perPage.toString(),
        page: (this.$route.query.page as string) || this.page.toString(),
        sortColumn: this.sortColumn,
        sortOrder: this.sortOrder
      }
    });
  }

  public async handlePaginator({
    perPage,
    page
  }: {
    perPage: number;
    page: number;
  }) {
    if (this.$route.query.tab !== 'Groups') {
      return;
    }
    this.perPage = perPage || +this.$route.query.limit;
    this.page = page || +this.$route.query.page;

    if (
      this.filterParams.legalName?.length ||
      this.filterParams.region?.length ||
      this.filterParams.country?.length
    ) {
      this.expandGroupFilter = true;
    }

    const params: JbicGroupParams = {
      legalName:
        (this.$route.query.legalName as string) || this.filterParams.legalName,
      region: (this.$route.query.region as string) || this.filterParams.region,
      country:
        (this.$route.query.country as string) || this.filterParams.country,
      status: (this.$route.query.status as string) || this.filterParams.status,
      limit: this.perPage,
      page: this.page,
      sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
      sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
    };

    this.getJbicGroupsWithPagination(params);

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Groups',
        legalName: this.$route.query.legalName || this.filterParams.legalName!,
        region: this.$route.query.region || this.filterParams.region!,
        country: this.$route.query.country || this.filterParams.country!,
        status: this.$route.query.status || this.filterParams.status!,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
      }
    });

    this.expandGroupFilter = false;

    if (
      this.filterParams.legalName?.length ||
      this.filterParams.region?.length ||
      this.filterParams.country?.length
    ) {
      this.expandGroupFilter = true;
    }
  }
}
